import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Check, Download, Eye, Mail, Upload, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface RecordJournalPhase1Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export function RecordJournalPhase1({ task, phaseData, onPhaseComplete }: RecordJournalPhase1Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [generating, setGenerating] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [unaData, setUnaData] = useState<any | null>(null);
  const [treasurySecretaryProfile, setTreasurySecretaryProfile] = useState<any | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [pendingMemberSignatures, setPendingMemberSignatures] = useState<Record<string, boolean>>({});

  const workflowData = task.workflow_data || {};
  const recordType = workflowData.record_type || '';
  const isResolution = recordType === 'Resolution by Written Consent';

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  useEffect(() => {
    if (documents.length > 0 && isResolution) {
      checkPendingSignatures();
    }
  }, [documents, isResolution]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          status
        )
      `)
      .eq('id', task.una_id)
      .maybeSingle();

    if (!error && data) {
      setUnaData(data);
      
      if (data.treasury_secretary_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('id', data.treasury_secretary_id)
          .single();
        
        if (profileData) {
          setTreasurySecretaryProfile(profileData);
        }
      }
    }
  }

  async function loadDocuments() {
    const { data, error } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true });

    if (error) {
      console.error('Error loading documents:', error);
      return;
    }

    setDocuments(data || []);
  }

  async function checkPendingSignatures() {
    const pending: Record<string, boolean> = {};
    
    for (const doc of documents) {
      if (doc.status === 'finalized') {
        const { data: sigRequests } = await supabase
          .from('signature_requests')
          .select('id, status')
          .eq('document_id', doc.id)
          .eq('status', 'pending');
        
        pending[doc.id] = (sigRequests && sigRequests.length > 0);
      }
    }
    
    setPendingMemberSignatures(pending);
  }

  function getRecordPrefix(type: string): string {
    switch (type) {
      case 'Private Contribution Received': return 'RCP';
      case 'Grant or Distribution Made': return 'DST';
      case 'Expense Payment': return 'EXP';
      case 'Resolution by Written Consent': return 'RES';
      case 'Activity Log Entry': return 'LOG';
      case 'General Record': return 'JNL';
      default: return 'REC';
    }
  }

  async function generateRecordNumber(): Promise<string> {
    const prefix = getRecordPrefix(recordType);
    const year = new Date().getFullYear();
    
    // Query existing records for this UNA with the same prefix and year
    const { data: existingRecords } = await supabase
      .from('una_journal_records')
      .select('record_number')
      .eq('una_id', task.una_id)
      .like('record_number', `${prefix}-${year}-%`);
    
    // Find the highest sequence number
    let maxSequence = 0;
    if (existingRecords && existingRecords.length > 0) {
      existingRecords.forEach((record: any) => {
        if (record.record_number) {
          const match = record.record_number.match(/-(\d{4})$/);
          if (match) {
            const seq = parseInt(match[1], 10);
            if (seq > maxSequence) maxSequence = seq;
          }
        }
      });
    }
    
    // Generate the next number
    const nextSequence = (maxSequence + 1).toString().padStart(4, '0');
    return `${prefix}-${year}-${nextSequence}`;
  }

  function getRecordTitle(): string {
    // Generate a meaningful title based on record type and available data
    if (workflowData.record_title) return workflowData.record_title;
    
    switch (recordType) {
      case 'Private Contribution Received':
        return workflowData.contributor_name ? `Contribution from ${workflowData.contributor_name}` : 'Private Contribution';
      case 'Grant or Distribution Made':
        return workflowData.recipient_name ? `Distribution to ${workflowData.recipient_name}` : 'Distribution Record';
      case 'Expense Payment':
        return workflowData.payee_name ? `Payment to ${workflowData.payee_name}` : 'Expense Payment';
      case 'Activity Log Entry':
        return workflowData.activity_description?.substring(0, 50) || 'Activity Log Entry';
      case 'General Record':
        return workflowData.record_content?.substring(0, 50) || 'General Record';
      default:
        return 'Record';
    }
  }

  function getDocumentName(): string {
    const recordTitle = getRecordTitle();
    
    switch (recordType) {
      case 'Private Contribution Received':
        return `Contribution Receipt - ${workflowData.contributor_name || recordTitle}`;
      case 'Grant or Distribution Made':
        return `Distribution Record - ${workflowData.recipient_name || recordTitle}`;
      case 'Expense Payment':
        return `Expense Record - ${workflowData.payee_name || recordTitle}`;
      case 'Resolution by Written Consent':
        return `Resolution - ${recordTitle}`;
      case 'Activity Log Entry':
        return `Activity Log - ${recordTitle}`;
      case 'General Record':
        return `Record - ${recordTitle}`;
      default:
        return recordTitle;
    }
  }

  function generateDocumentHtml(recordNumber: string): string {
    const formatDate = (dateStr: string) => {
      if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    const unaName = unaData?.name || '[UNA Name]';
    const recordDate = formatDate(workflowData.record_date);
    const treasurySecretaryName = treasurySecretaryProfile?.full_name || '[Secretary Treasurer]';
    const treasurySecretaryId = unaData?.treasury_secretary_id || 'treasury_secretary';

    const baseStyles = `
      @page { 
        size: letter; 
        margin: 1in; 
        @bottom-center { 
          content: "Page " counter(page) " of " counter(pages); 
          font-family: Cambria, Georgia, serif; 
          font-size: 10pt; 
          padding-bottom: 0.25in; 
        } 
      }
      body { 
        font-family: Cambria, Georgia, serif; 
        line-height: 1.6; 
        color: #333; 
        margin: 0; 
        padding: 0; 
      }
      .header { 
        text-align: center; 
        margin-bottom: 30px; 
        border-bottom: 2px solid #333;
        padding-bottom: 20px;
      }
      .header h1 { 
        font-size: 16pt; 
        font-weight: bold; 
        margin-bottom: 10px;
      }
      .header h2 { 
        font-size: 14pt; 
        font-weight: normal;
        font-style: italic;
        color: #666;
        margin-bottom: 5px; 
      }
      .content { margin-bottom: 20px; }
      .info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      .info-table td { padding: 8px; border: 1px solid #ccc; }
      .info-table td:first-child { width: 35%; background-color: #f5f5f5; font-weight: bold; }
      .section { margin: 20px 0; padding: 15px; border: 1px solid #ccc; }
      .section-title { font-weight: bold; margin-bottom: 10px; }
      .signature-block { margin-top: 40px; page-break-inside: avoid; }
      .signature-line { border-bottom: 2px solid #333; min-height: 0.6in; width: 3.5in; margin-bottom: 0.15in; padding: 0.1in; }
      .signature-label { font-size: 11pt; margin-top: 0.1in; }
      .note { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-left: 4px solid #666; font-size: 11pt; }
      .confidential { border: 2px solid #d9534f; padding: 10px; margin-bottom: 20px; background-color: #f2dede; text-align: center; }
      .follow-up { border: 2px solid #f0ad4e; padding: 15px; margin: 20px 0; background-color: #fcf8e3; }
    `;

    // Private Contribution Receipt
    if (recordType === 'Private Contribution Received') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Private Contribution Receipt</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; text-decoration: underline; margin-bottom: 30px;">PRIVATE CONTRIBUTION RECEIPT</h2>
  
  <table class="info-table">
    <tr><td>Receipt Date</td><td>${recordDate}</td></tr>
    <tr><td>Receipt Number</td><td>${recordNumber}</td></tr>
  </table>
  
  <div class="section">
    <div class="section-title">Received From:</div>
    <p><strong>Name:</strong> ${workflowData.contributor_name || ''}</p>
    ${workflowData.contributor_address ? `<p><strong>Address:</strong> ${workflowData.contributor_address}</p>` : ''}
  </div>
  
  <div class="section">
    <div class="section-title">Contribution Details:</div>
    <p><strong>Amount:</strong> ${formatCurrency(workflowData.amount)}</p>
    <p><strong>Purpose:</strong> ${workflowData.contribution_purpose || ''}</p>
    <p><strong>Method:</strong> ${workflowData.contribution_method || ''}</p>
    ${workflowData.transaction_description ? `<p><strong>Description:</strong> ${workflowData.transaction_description}</p>` : ''}
  </div>
  
  <div class="note">
    <p>This contribution was received as a private contribution to <strong>${unaName}</strong>, an Unincorporated Nonprofit Association operating under ecclesiastical jurisdiction. This receipt is provided for the contributor's records.</p>
    <p style="font-style: italic; margin-top: 10px;">Note: This is not a tax-deductible charitable donation receipt. Please consult your tax advisor regarding any potential tax implications of this private contribution.</p>
  </div>
  
  <div class="signature-block">
    <p><strong>Received and Acknowledged By:</strong></p>
    <div class="signature-line" data-signer-id="${treasurySecretaryId}" data-signature-role="treasury_secretary"></div>
    <div class="signature-label">${treasurySecretaryName}, ${unaData?.primary_officer_role || 'Secretary Treasurer'}</div>
    <div style="margin-top: 0.15in;">Date: ${recordDate}</div>
  </div>
</body>
</html>`;
    }

    // Grant/Distribution Record
    if (recordType === 'Grant or Distribution Made') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Record of Distribution</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; text-decoration: underline; margin-bottom: 30px;">RECORD OF DISTRIBUTION</h2>
  
  <table class="info-table">
    <tr><td>Distribution Date</td><td>${recordDate}</td></tr>
    <tr><td>Record Number</td><td>${recordNumber}</td></tr>
  </table>
  
  <div class="section">
    <div class="section-title">Recipient Information:</div>
    <p><strong>Recipient:</strong> ${workflowData.recipient_name || ''}</p>
    ${workflowData.recipient_relationship ? `<p><strong>Relationship:</strong> ${workflowData.recipient_relationship}</p>` : ''}
  </div>
  
  <div class="section">
    <div class="section-title">Distribution Details:</div>
    <p><strong>Amount:</strong> ${formatCurrency(workflowData.amount)}</p>
    <p><strong>Purpose:</strong> ${workflowData.distribution_purpose || ''}</p>
    <p><strong>Method:</strong> ${workflowData.distribution_method || ''}</p>
    ${workflowData.authorization_reference ? `<p><strong>Authorization:</strong> ${workflowData.authorization_reference}</p>` : ''}
    ${workflowData.transaction_description ? `<p><strong>Notes:</strong> ${workflowData.transaction_description}</p>` : ''}
  </div>
  
  <div class="signature-block">
    <p><strong>Authorized By:</strong></p>
    <div class="signature-line" data-signer-id="${treasurySecretaryId}" data-signature-role="treasury_secretary"></div>
    <div class="signature-label">${treasurySecretaryName}, ${unaData?.primary_officer_role || 'Secretary Treasurer'}</div>
    <div style="margin-top: 0.15in;">Date: ${recordDate}</div>
  </div>
  
  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px dashed #ccc;">
    <p><strong>Recipient Acknowledgment (Optional):</strong></p>
    <p style="font-size: 11pt;">I acknowledge receipt of the above distribution.</p>
    <div style="margin-top: 30px;">
      <div style="border-bottom: 1px solid #000; width: 300px; display: inline-block;"></div>
      <p style="margin: 5px 0;">Recipient Signature</p>
      <p style="margin: 5px 0;">Date: _________________</p>
    </div>
  </div>
</body>
</html>`;
    }

    // Expense Payment Record
    if (recordType === 'Expense Payment') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Expense Payment Record</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; text-decoration: underline; margin-bottom: 30px;">EXPENSE PAYMENT RECORD</h2>
  
  <table class="info-table">
    <tr><td>Payment Date</td><td>${recordDate}</td></tr>
    <tr><td>Record Number</td><td>${recordNumber}</td></tr>
    <tr><td>Payee</td><td>${workflowData.payee_name || ''}</td></tr>
    <tr><td>Amount</td><td><strong>${formatCurrency(workflowData.amount)}</strong></td></tr>
    <tr><td>Category</td><td>${workflowData.expense_category || ''}</td></tr>
    <tr><td>Payment Method</td><td>${workflowData.payment_method || ''}</td></tr>
    <tr><td>Recurring</td><td>${workflowData.is_recurring ? 'Yes' : 'No'}</td></tr>
  </table>
  
  ${workflowData.transaction_description ? `
  <div class="section">
    <div class="section-title">Description:</div>
    <p>${workflowData.transaction_description}</p>
  </div>` : ''}
  
  <div class="signature-block">
    <p><strong>Authorized By:</strong></p>
    <div class="signature-line" data-signer-id="${treasurySecretaryId}" data-signature-role="treasury_secretary"></div>
    <div class="signature-label">${treasurySecretaryName}, ${unaData?.primary_officer_role || 'Secretary Treasurer'}</div>
    <div style="margin-top: 0.15in;">Date: ${recordDate}</div>
  </div>
</body>
</html>`;
    }

    // Resolution by Written Consent
    if (recordType === 'Resolution by Written Consent') {
      const approvingOfficers = workflowData.approving_officers || [];
      const officerSignatureBlocks = approvingOfficers.map((officer: any) => `
        <div class="signature-block">
          <p><strong>Officer Approval:</strong></p>
          <div class="signature-line" data-signer-id="${officer.id}" data-signature-role="approving_officer"></div>
          <div class="signature-label">${officer.name}, ${officer.role}</div>
          <div style="margin-top: 0.15in;">Date: _________________</div>
        </div>
      `).join('\n');

      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Resolution by Written Consent</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; font-size: 14pt; margin-bottom: 5px;">RESOLUTION BY WRITTEN CONSENT</h2>
  <h3 style="text-align: center; font-size: 12pt; font-weight: normal; margin-bottom: 30px;">
    OF THE OFFICERS OF ${unaName}<br/>IN LIEU OF A MEETING
  </h3>
  
  <table class="info-table">
    <tr><td>Resolution Number</td><td>${recordNumber}</td></tr>
    <tr><td>Date Adopted</td><td>${recordDate}</td></tr>
    <tr><td>Effective Date</td><td>${formatDate(workflowData.effective_date || workflowData.record_date)}</td></tr>
  </table>
  
  <div style="margin: 25px 0;">
    <h3 style="text-decoration: underline;">RE: ${workflowData.record_title || 'Untitled Resolution'}</h3>
  </div>
  
  <div style="margin-bottom: 20px; text-align: justify;">
    ${(workflowData.whereas_clauses || '').split('\n').filter((c: string) => c.trim()).map((clause: string) => 
      `<p style="margin-bottom: 12px;"><strong>WHEREAS,</strong> ${clause.replace(/^WHEREAS,?\s*/i, '').trim()};</p>`
    ).join('\n')}
  </div>
  
  <div style="margin-bottom: 30px; text-align: justify;">
    <p><strong>NOW, THEREFORE, BE IT</strong></p>
    ${(workflowData.resolved_that || '').split('\n').filter((c: string) => c.trim()).map((clause: string) => 
      `<p style="margin-bottom: 12px;"><strong>RESOLVED,</strong> ${clause.replace(/^RESOLVED,?\s*/i, '').trim()}.</p>`
    ).join('\n')}
  </div>
  
  <div class="note">
    <strong>CERTIFICATION:</strong> The undersigned, being the authorized officer(s) of ${unaName}, hereby consent to the adoption of the foregoing resolution without a meeting, pursuant to the authority granted in the UNA Constitution and Bylaws. This resolution shall have the same force and effect as if adopted at a duly convened meeting.
  </div>
  
  <div class="signature-block">
    <p><strong>Approved and Adopted by ${unaData?.primary_officer_role || 'Secretary Treasurer'}:</strong></p>
    <div class="signature-line" data-signer-id="${treasurySecretaryId}" data-signature-role="treasury_secretary"></div>
    <div class="signature-label">${treasurySecretaryName}, ${unaData?.primary_officer_role || 'Secretary Treasurer'}</div>
    <div style="margin-top: 0.15in;">Date: ${recordDate}</div>
  </div>
  
  ${officerSignatureBlocks}
</body>
</html>`;
    }

    // Activity Log Entry
    if (recordType === 'Activity Log Entry') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Activity Log</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; text-decoration: underline; margin-bottom: 30px;">ACTIVITY LOG</h2>
  
  <table class="info-table">
    <tr><td>Date</td><td>${recordDate}</td></tr>
    <tr><td>Log Number</td><td>${recordNumber}</td></tr>
    <tr><td>Title</td><td>${getRecordTitle()}</td></tr>
    <tr><td>Category</td><td>${workflowData.activity_category || ''}</td></tr>
  </table>
  
  <div class="section">
    <div class="section-title">Activity Description:</div>
    <div style="background-color: #fafafa; padding: 15px;">
      ${workflowData.activity_description || ''}
    </div>
  </div>
  
  ${workflowData.follow_up_required ? `
  <div class="follow-up">
    <h4 style="margin-top: 0; color: #8a6d3b;">⚠ Follow-Up Required</h4>
    <p><strong>Follow-Up Date:</strong> ${formatDate(workflowData.follow_up_date)}</p>
    ${workflowData.follow_up_notes ? `<p><strong>Notes:</strong> ${workflowData.follow_up_notes}</p>` : ''}
  </div>` : ''}
  
  <div style="margin-top: 30px;">
    <p><strong>Recorded By:</strong> ${workflowData.authorized_by || treasurySecretaryName}</p>
    <p><strong>Date Recorded:</strong> ${recordDate}</p>
  </div>
</body>
</html>`;
    }

    // General Record
    if (recordType === 'General Record') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Official Record</title>
  <style>${baseStyles}</style>
</head>
<body>
  <div class="header">
    <h1>${unaName}</h1>
    <h2>An Unincorporated Nonprofit Association</h2>
  </div>
  
  <h2 style="text-align: center; text-decoration: underline; margin-bottom: 30px;">OFFICIAL RECORD</h2>
  
  ${workflowData.is_confidential ? `
  <div class="confidential">
    <strong style="color: #a94442;">🔒 CONFIDENTIAL - RESTRICTED ACCESS</strong>
  </div>` : ''}
  
  <table class="info-table">
    <tr><td>Record Date</td><td>${recordDate}</td></tr>
    <tr><td>Record Number</td><td>${recordNumber}</td></tr>
    <tr><td>Title</td><td>${getRecordTitle()}</td></tr>
    <tr><td>Category</td><td>${workflowData.record_category || ''}</td></tr>
  </table>
  
  <div class="section">
    <div class="section-title">Record Content:</div>
    <div style="background-color: #fafafa; padding: 20px;">
      ${workflowData.record_content || ''}
    </div>
  </div>
  
  <div style="margin-top: 30px;">
    <p><strong>Authorized By:</strong> ${workflowData.authorized_by || treasurySecretaryName}</p>
    <p><strong>Date:</strong> ${recordDate}</p>
  </div>
</body>
</html>`;
    }

    return '';
  }

  async function handleGenerateDocument() {
    setGenerating(true);
    
    try {
      // Generate the record number first
      const recordNumber = await generateRecordNumber();
      const htmlContent = finishLegacyHtml(generateDocumentHtml(recordNumber), {
        entityName: unaData?.name,
        sealEnabled: seal.sealEnabled,
        sealSvg: seal.sealSvg,
      });

      if (!htmlContent) {
        throw new Error('Failed to generate document content');
      }

      // Generate PDF
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: htmlContent, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'PDF generation failed');
      }

      const { pdf: pdfBase64 } = await response.json();

      // Convert to blob and upload
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      const documentName = getDocumentName();
      const filePath = generatedFilePath(unaData?.name, 'journal_record', task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      // Insert document record
      const { data: newDoc, error: insertError } = await supabase
        .from('generated_documents')
        .insert({
          task_id: task.id,
          una_id: task.una_id,
          document_name: documentName,
          document_type: recordType.toLowerCase().replace(/\s+/g, '_'),
          file_path: filePath,
          file_url: filePath,
          status: isResolution ? 'draft' : 'final',
          custom_content: htmlContent,
          merge_field_data: { 
            record_type: recordType,
            record_number: recordNumber,
            workflow_data: workflowData 
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: "Document Generated",
        description: `${documentName} has been created successfully.`,
      });

      loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate document",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  }

  async function handleTreasurySecretarySignature(signatureData: string) {
    if (!selectedDocForSignature) return;

    try {
      // Fetch fresh document content
      const { data: freshDoc } = await supabase
        .from('generated_documents')
        .select('custom_content')
        .eq('id', selectedDocForSignature.id)
        .single();

      if (!freshDoc?.custom_content) {
        throw new Error('Document content not found');
      }

      const signatures = [{
        role: 'treasury_secretary',
        signer_id: unaData?.treasury_secretary_id,
        signature_data: signatureData,
        signed_at: new Date().toISOString()
      }];

      const signedHtml = await embedSignaturesIntoHtml(
        freshDoc.custom_content,
        signatures,
        new Date().toISOString()
      );

      // Generate new PDF
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: signedHtml, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) throw new Error('PDF generation failed');

      const { pdf: pdfBase64 } = await response.json();
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      const filePath = generatedFilePath(unaData?.name, 'journal_record_signed', task.id);

      await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      // Buckets are private — store path as reference

      // Update document
      await supabase
        .from('generated_documents')
        .update({
          file_path: filePath,
          file_url: filePath,
          signed_file_url: filePath,
          custom_content: signedHtml,
          status: 'finalized',
          finalized_at: new Date().toISOString(),
        })
        .eq('id', selectedDocForSignature.id);

      toast({
        title: "Document Signed",
        description: `${unaData?.primary_officer_role || 'Secretary Treasurer'} signature has been applied.`,
      });

      setSignatureWizardOpen(false);
      setSelectedDocForSignature(null);
      loadDocuments();
    } catch (error: any) {
      console.error('Error signing document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to sign document",
        variant: "destructive",
      });
    }
  }

  async function handleDownload(doc: any) {
    const url = doc.signed_file_url || doc.file_url;
    if (url) {
      window.open(url, '_blank');
    }
  }

  function getDocumentStatus(doc: any) {
    if (doc.status === 'finalized') {
      if (pendingMemberSignatures[doc.id]) {
        return { label: 'Awaiting Member Signatures', variant: 'secondary' as const };
      }
      return { label: 'Signed & Complete', variant: 'default' as const };
    }
    if (doc.status === 'final') {
      return { label: 'Generated', variant: 'outline' as const };
    }
    return { label: 'Needs Signature', variant: 'secondary' as const };
  }

  const doc = documents[0];
  const hasDocument = documents.length > 0;
  const isComplete = hasDocument && (doc?.status === 'finalized' || !isResolution);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Generate Record Document</CardTitle>
          <CardDescription>
            Create the official {recordType.toLowerCase()} document based on your entered data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasDocument ? (
            <Button onClick={handleGenerateDocument} disabled={generating || !recordType}>
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Document
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {documents.map((doc) => {
                const status = getDocumentStatus(doc);
                return (
                  <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{doc.document_name}</p>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPreviewDocument(doc)}>
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownload(doc)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setSelectedDocForEmail(doc.id);
                          setEmailWizardOpen(true);
                        }}
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Email
                      </Button>
                      
                      {/* Sign & Finalize for Resolution documents */}
                      {isResolution && doc.status === 'draft' && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            setSelectedDocForSignature(doc);
                            setSignatureWizardOpen(true);
                          }}
                        >
                          Sign & Finalize
                        </Button>
                      )}
                      
                      {/* Request Member Signatures after Secretary Treasurer signs */}
                      {doc.merge_field_data?.requires_signature !== false && task.una_id && (
                          <div className="w-full">
                            <DocumentSigningControl
                              doc={doc}
                              unaId={task.una_id}
                              sealEnabled={seal.sealAvailable}
                              onChanged={loadDocuments}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Supporting Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Supporting Documents</CardTitle>
          <CardDescription>Upload receipts, invoices, or other supporting documentation.</CardDescription>
        </CardHeader>
        <CardContent>
          {doc && (
            <DocumentUploadButton
              documentId={doc.id}
              documentName={doc.document_name}
              onUploadComplete={loadDocuments}
            />
          )}
        </CardContent>
      </Card>

      {/* Phase Completion */}
      <Alert className={isComplete ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}>
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <FileText className="h-5 w-5 text-muted-foreground" />
            )}
            <span>
              {isComplete 
                ? 'Document generated and ready for filing.'
                : isResolution
                  ? 'Generate and sign the resolution document to proceed.'
                  : 'Generate the record document to proceed.'
              }
            </span>
          </div>
          <Button onClick={onPhaseComplete} disabled={!isComplete}>
            Continue to Filing
          </Button>
        </AlertDescription>
      </Alert>

      {/* Modals */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {signatureWizardOpen && selectedDocForSignature && (
        <FinalizeWithSignatures
          documentTitle={`Sign: ${selectedDocForSignature.document_name}`}
          requiredSigners={['treasury_secretary']}
          treasurySecretaryName={treasurySecretaryProfile?.full_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          onFinalize={async (signatures) => {
            if (signatures.treasury_secretary_signature) {
              await handleTreasurySecretarySignature(signatures.treasury_secretary_signature);
            }
          }}
          onCancel={() => {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }}
        />
      )}

      {emailWizardOpen && selectedDocForEmail && (
        <DocumentEmailWizard
          preSelectedDocumentId={selectedDocForEmail}
          unaId={task.una_id}
          taskId={task.id}
          open={emailWizardOpen}
          onOpenChange={setEmailWizardOpen}
        />
      )}
    </div>
  );
}
